import React, { useMemo } from 'react'

import { Box, Display, Heading5, Space, Visible } from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import { RecipeTile } from '@features/recipe-tile'
import type { MenuPreferences } from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useNumPortions } from 'hooks/basket/useNumPortions'
import { useCurrentCollectionId } from 'hooks/menu-collections'
import { SearchRecipeTile } from 'routes/Menu/components/Search/SearchRecipeTile/SearchRecipeTile'
import type { AddRecipeByUuidFn, RemoveRecipeByUuidFn } from 'routes/Menu/domains/basket'
import { CollectionId } from 'routes/Menu/domains/collections'
import { useCollectionRecipes } from 'routes/Menu/domains/menu'
import { getIsAuthenticated } from 'selectors/auth'
import { getBasketMenuUuid } from 'selectors/basket'
import { getUserFirstName } from 'selectors/user'

import type { TrackCTAClick } from '../types'

type SuggestedRecipeProps = {
  addRecipe: AddRecipeByUuidFn
  canAddRecipes: boolean
  menuPreferences?: MenuPreferences
  onClick: (recipeId: RecipeUUID) => void
  reachedLimit: boolean
  removeRecipe: RemoveRecipeByUuidFn
}

export const SuggestedRecipe = ({
  addRecipe,
  canAddRecipes,
  menuPreferences,
  onClick,
  reachedLimit,
  removeRecipe,
}: SuggestedRecipeProps) => {
  const isAuthenticated = useSelector(getIsAuthenticated)
  const currentCollectionId = useCurrentCollectionId()

  const userFirstName = useSelector(getUserFirstName)
  const menuId = useSelector(getBasketMenuUuid)
  const { numPortions } = useNumPortions()

  const recommendationsRecipes = useCollectionRecipes(CollectionId.Recommendations)
  const recipe = useMemo(() => {
    if (recommendationsRecipes.length === 0) {
      return null
    }

    const [firstRecipe] = recommendationsRecipes

    return firstRecipe.recipe
  }, [recommendationsRecipes])

  const coreId = recipe?.coreId || null

  const { basketHasRecipe } = useBasketRecipes()

  if (!isAuthenticated || !recipe) return null

  return (
    <Box>
      <Heading5>Suggested For {userFirstName}</Heading5>
      <Space size={4} />
      <Visible display={[Display.None, Display.Block]}>
        <SearchRecipeTile
          addRecipe={(recipeId) => addRecipe(recipeId, 'menu_search_screen')}
          menuPreferences={menuPreferences}
          showCustomisedTileFooter={isAuthenticated}
          onClick={() => onClick(recipe.id)}
          reachedLimit={reachedLimit}
          recipe={recipe}
          removeRecipe={removeRecipe}
          trackingData={{ menuId }}
        />
      </Visible>
      <Visible display={[Display.Block, Display.None]}>
        <RecipeTile
          recipe={recipe}
          addRecipe={(recipeId) => {
            queueTrackingEvent<TrackCTAClick>({
              action: 'add_recipe',
              properties: {
                menu_id: menuId,
                add_type: 'search',
                recipe_id: recipe.id,
                event_screen: 'menu_search_screen',
              },
            })
            addRecipe(recipeId, 'menu_search_screen')
          }}
          alternativeOptions={{ optionsType: 'none', options: [] }}
          canAddRecipes={canAddRecipes}
          currentCollectionId={currentCollectionId}
          menuId={menuId}
          isInBasket={!!coreId && basketHasRecipe(coreId)}
          isOutOfStock={false}
          onClick={() => onClick(recipe.id)}
          originalId={recipe.coreId}
          reachedLimit={reachedLimit}
          recipeReference=""
          removeRecipe={(recipeId) => {
            queueTrackingEvent<TrackCTAClick>({
              action: 'remove_recipe',
              properties: {
                menu_id: menuId,
                add_type: 'search',
                recipe_id: recipe.id,
                event_screen: 'menu_search_screen',
              },
            })
            removeRecipe(recipeId)
          }}
          selectVariant={() => {}}
          setBrowserCTAVisible={() => {}}
          menuPreferences={menuPreferences}
          showCustomisedTileFooter={isAuthenticated}
          insideOverflow
          numPortions={numPortions}
        />
      </Visible>
    </Box>
  )
}
